import { appSelect } from '@entities/app';
import { authInfoSelect } from '@entities/auth';
import { IBookingReportResponse } from '@types';
import { API_DATE_FORMAT } from '@utils/const';
import { transformBookingData } from '@utils/functions/transformBookingData';
import { Chart, Filler } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import BookingSelector from '../components/BookingSelector';
import DiffInfo from '../components/DiffInfo';
import * as S from './style.styled';

Chart.register(Filler, annotationPlugin);

const selectOptions = [
    { key: 'all', value: 'all', label: 'Всего' },
    { key: 'approved', value: 'approved', label: 'Подтвержденных' },
    { key: 'declined', value: 'declined', label: 'Отклоненных' },
];

type BookingDataType = {
    labels: string[];
    datasets: BookingDataSets[];
    diff: number;
    total: number;
};

type BookingDataSets = {
    label: string;
    data: number[];
    fill: {
        target: string;
        above: string;
        below: string;
    };
    borderColor: string;
    tension: number;
};

const fill = {
    target: 'origin',
    above: 'rgba(148, 129, 205, 0.1)',
    below: 'rgba(148, 129, 205, 0.1)',
};
const borderColor = 'rgba(148, 129, 205, 0.25)';
const initialValue: BookingDataType = {
    labels: [],
    datasets: [
        {
            label: '',
            data: [],
            fill,
            borderColor,
            tension: 0.4,
        },
    ],
    diff: 0,
    total: 0,
};

const options = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
        padding: 0,
    },
    scales: {
        x: {
            grid: {
                display: false, // Убираем линии сетки по оси X
            },
            ticks: {
                display: true,
                // color: "gray", // Цвет текста меток
                // backdropPadding: 2, // Отступы внутри фона меток
                beginAtZero: true,
            },
            offset: false,
            border: {
                display: false,
            },
        },
        y: {
            display: false,
            grid: {
                display: false, // Убираем линии сетки по оси X
            },
            ticks: {
                display: false, // Убираем подписи по оси Y
                beginAtZero: true,
            },
        },
    },
    plugins: {
        legend: {
            display: false, // Убираем легенду
        },
        hover: {
            mode: 'nearest',
            intersect: true,
            onHover: (event, chartElement) => {
                const chart = chartElement[0]?.element?.$context?.chart;
                if (chart) {
                    const index = chartElement[0].index;
                    const xAxis = chart.scales.x;
                    xAxis.ticks[index].label = {
                        ...xAxis.ticks[index].label,
                        backgroundColor: 'blue',
                        color: 'white',
                        padding: 2,
                        borderRadius: 3,
                    };
                    chart.update();
                }
            },
        },
    },
};

export function Plag1() {
    const { shop } = useSelector(authInfoSelect);
    const { from, to } = useSelector(appSelect).dateRange;

    const [datas, setDatas] = useState<BookingDataType>(initialValue);
    const [select, setSelect] = useState<number>(0);

    const urlForBookingPlagin = useMemo(() => {
        if (!shop?.id) return null;

        const startDate = from
            ? dayjs(from, API_DATE_FORMAT).format(API_DATE_FORMAT)
            : '2024-01-01';
        const endDate = to
            ? dayjs(to, API_DATE_FORMAT).format(API_DATE_FORMAT)
            : '2024-11-01';
        const state = selectOptions[select].key;

        return `${process.env.REACT_APP_API_URL}/shops/${shop.id}/reports/booking?from=${startDate}&to=${endDate}&state=${state}`;
    }, [from, to, select, shop]);

    useEffect(() => {
        if (!urlForBookingPlagin) return;

        fetch(urlForBookingPlagin, {
            headers: {
                accept: 'application/json',
                Authorization: `${localStorage.getItem('accessToken')}`,
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.json())
            .then(data => {
                setDatas(initialValue);
                if (data && data.rows) {
                    const info = transformBookingData(
                        data as IBookingReportResponse
                    );
                    setDatas({
                        labels: info.labels,
                        datasets: [
                            {
                                label: '',
                                data: info.data,
                                fill,
                                borderColor,
                                tension: 0.4,
                            },
                        ],
                        diff: info.diff,
                        total: info.total,
                    });
                }
            })
            .catch(err =>
                console.error('Error fetching booking report data:', err)
            );
    }, [urlForBookingPlagin]);

    return (
        <S.Palg1Wrap>
            <S.StyledHeader>
                <S.Header>Бронирование</S.Header>
                <S.WrapSelect>
                    <BookingSelector
                        select={select}
                        setSelect={setSelect}
                        selectOptions={selectOptions}
                    />
                </S.WrapSelect>
            </S.StyledHeader>
            <S.CharWrap>
                <Line data={datas} options={options} />
            </S.CharWrap>
            <S.DiffWrap>
                <DiffInfo diff={datas?.diff} total={datas?.total} />
            </S.DiffWrap>
        </S.Palg1Wrap>
    );
}
